import { DateTimeUtils } from '@utils/datetime-util';
import * as pdfseira from '@utils/pdf-seira';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import {
	getXLSXDefinitions,
	getXLSXSheetDefinitions,
	getXLSXSheetTitleDefinitions,
} from '@utils/xlsx-seira';
import { format } from 'date-fns';
import { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { alinharDado, isNotNuloOuUndefined, isNuloOuUndefined } from '@utils';
import { NumberUtils } from '@utils/number-utils';
import {
	ConfigColuna,
	ConfigColunaXLSX,
	DTOExportarCSV,
	DTOExportarMultiplasTabelasXLSX,
	DTOExportarPDF,
	DTOExportarTXT,
	DTOExportarXLSX,
	DTOGetDadoCelula,
	DTOGetDadosLinha,
	DTOPrepararDadosParaATabela,
	ImagemPDF,
} from '../types/dtos-funcoes-exportacao';

export async function exportarPDF(dtoPDF: DTOExportarPDF) {
	const {
		colunasAEsquerda,
		periodoAgrupamento,
		resultados,
		tabela,
		tituloTabela,
		tituloMunicipio,
		imagem,
		colunasOcultas,
		configsColunas = [],
		orientacao,
	} = dtoPDF;

	const documentDefinition = await pdfseira.documentDefinitions(
		orientacao ?? 'landscape'
	);

	adicionarTitulo(documentDefinition, tituloTabela);

	if (tituloMunicipio) {
		adicionarTitulo(documentDefinition, tituloMunicipio, 'center');
	}

	if (imagem) {
		adicionarImagem(imagem, documentDefinition);
	}

	if (temDadosParaTabela(resultados, tabela)) {
		const tableData = prepararDadosParaTabela({
			tabela,
			periodoAgrupamento,
			isPdf: true,
			colunasAEsquerda: colunasAEsquerda ?? [0],
			colunasOcultas: colunasOcultas ?? [],
			configsColunas,
		});

		if (
			tabela &&
			isNotNuloOuUndefined(colunasOcultas) &&
			colunasOcultas?.length > 0
		) {
			const tabelaTratada = getTabelaComColunasOcultadas(
				tabela,
				colunasOcultas
			);
			adicionarTabelaAoDocumento(tabelaTratada, documentDefinition, tableData);
		} else {
			adicionarTabelaAoDocumento(tabela, documentDefinition, tableData);
		}
	} else {
		adicionarMensagemSemDados(documentDefinition);
	}

	gerarDocumentoPDF(documentDefinition, tituloTabela);
}

function getTabelaComColunasOcultadas(
	tabela: PublicTableComponent,
	colunasOcultas: number[]
): PublicTableComponent {
	const tabelaRetorno = { ...tabela };
	tabelaRetorno.columns = tabelaRetorno.columns?.filter(
		(_, indice) => !colunasOcultas.includes(indice)
	);
	if (tabelaRetorno?.dtOptions?.columns) {
		tabelaRetorno.dtOptions.columns = tabelaRetorno.dtOptions.columns.filter(
			(_, indice) => !colunasOcultas.includes(indice)
		);
	}
	if (tabelaRetorno.tabela?.dtOptions?.columns) {
		tabelaRetorno.tabela!.dtOptions.columns =
			tabelaRetorno.tabela!.dtOptions.columns?.filter(
				(_: any, indice: any) => !colunasOcultas.includes(indice)
			);
	}
	return tabelaRetorno as PublicTableComponent;
}

export function exportarCSV({
	tabela,
	periodoAgrupamento,
	tituloTabela,
	substituicaoPrimeiraColuna,
	colunasOcultas,
	configsColunas = [],
}: DTOExportarCSV) {
	const tableData = prepararDadosParaTabela({
		tabela,
		periodoAgrupamento,
		isPdf: false,
		colunasAEsquerda: [0],
		colunasOcultas: colunasOcultas ?? [],
		configsColunas,
	});

	if (substituicaoPrimeiraColuna) {
		tableData[0].unshift(substituicaoPrimeiraColuna.titulo);
		tableData
			.slice(1)
			.forEach((td: string[]) => td.unshift(substituicaoPrimeiraColuna.valor));
	}

	DocumentExporter.gerarCSV(tableData, tituloTabela);
}

export async function exportarTXT({
	periodoAgrupamento,
	tabela,
	titulo,
	tituloMunicipio,
	colunasOcultas,
	configsColunas = [],
}: DTOExportarTXT) {
	let txtData = '';
	const tableData = prepararDadosParaTabela({
		tabela,
		periodoAgrupamento,
		isPdf: false,
		colunasOcultas: colunasOcultas ?? [],
		configsColunas,
	});

	if (tableData.length > 0) {
		const colunas = tableData[0];

		if (tituloMunicipio) {
			txtData += `Município/Posto: ${tituloMunicipio}\n`;
			txtData += '\n';
		}

		tableData.slice(1).forEach(dadoRelatorio => {
			colunas.forEach((coluna: string, index: number) => {
				const valor = dadoRelatorio[index];
				txtData += `${coluna}: ${valor ?? '-'}\n`;
			});

			txtData += '\n';
		});
	}

	DocumentExporter.gerarTXT(txtData, titulo);
}

function getSheetColumns(
	tabela: PublicTableComponent,
	headers: {
		label: string;
		text: string;
		size: number;
	}[],
	configsColunas: ConfigColunaXLSX[]
) {
	return headers.map(header => {
		const colConfig: Record<string, any> = { key: header.label };
		const format = getFormatoColunaXLSX(tabela, header.label, configsColunas);

		if (format) {
			colConfig['style'] = { format };
		}

		return colConfig;
	});
}

function getFormatoColunaXLSX(
	tabela: PublicTableComponent,
	headerLabel: string,
	configsColunas: ConfigColunaXLSX[]
) {
	const indexColuna = tabela.columns.findIndex(c => c.data === headerLabel);
	const configColuna = configsColunas?.length
		? configsColunas.find(
				c => c.coluna === headerLabel || c.coluna === indexColuna
		  )
		: undefined;

	if (configColuna) {
		return getFormatoPorCasasDecimais(configColuna.casasDecimais);
	}

	return isColunaNumerica(tabela, indexColuna) ? 'num_sep_1' : undefined;
}

function getFormatoPorCasasDecimais(casasDecimais?: number) {
	switch (casasDecimais) {
		case 0:
			return 'num_sep';
		case 1:
			return 'num_sep_1';
		case 2:
			return 'num_sep_2';
		case 3:
			return 'float_3';
		case 4:
			return 'float_4';
		default:
			return undefined;
	}
}

function getStyleIdByFormat(
	format?: string,
	isColunaAlinhadaAEsquerda = false,
	destacar = false
): string | undefined {
	if (!format) {
		return undefined;
	}

	const prefixo = isColunaAlinhadaAEsquerda ? 'rowStyle' : 'rowStyleCenter';
	const sufixoDestaque = destacar ? 'NaoOficial' : '';

	switch (format) {
		case 'num_sep':
			return `${prefixo}Integer${sufixoDestaque}`;
		case 'num_sep_1':
		case 'float_1':
			return `${prefixo}Decimal1${sufixoDestaque}`;
		case 'num_sep_2':
			return `${prefixo}Decimal2${sufixoDestaque}`;
		case 'float_3':
			return `${prefixo}Decimal3${sufixoDestaque}`;
		case 'float_4':
			return `${prefixo}Decimal4${sufixoDestaque}`;
		default:
			return undefined;
	}
}

function getValorColunaDaLinha(linha: Record<string, any>, coluna: any) {
	if (!coluna || isNuloOuUndefined(coluna.data)) {
		return undefined;
	}

	if (typeof coluna.data === 'string' && coluna.data.includes('.')) {
		return coluna.data
			.split('.')
			.reduce(
				(valorAtual: any, chaveAtual: string) =>
					isNuloOuUndefined(valorAtual) ? undefined : valorAtual[chaveAtual],
				linha
			);
	}

	return linha[coluna.data];
}

function isColunaNumerica(
	tabela: PublicTableComponent,
	indexColuna: number
): boolean {
	if (indexColuna < 0) {
		return false;
	}

	const coluna = tabela.columns[indexColuna];

	return tabela.data.some((linha: Record<string, any>) => {
		const valor = getValorColunaDaLinha(linha, coluna);

		return (
			!isNuloOuUndefined(valor) &&
			valor !== '-' &&
			NumberUtils.ehConversivelEmNumber(String(valor))
		);
	});
}

export async function exportarXLSX(dtoXLSX: DTOExportarXLSX) {
	const {
		periodoAgrupamento,
		tituloTabela,
		colunasAEsquerda,
		tabela,
		tituloMunicipio,
		colunasOcultas,
		configsColunas = [],
		linhaEhDestacada,
		colunasDestacadas,
	} = dtoXLSX;
	if (!tabela) {
		return;
	}
	let tabelaParaExportar: PublicTableComponent;
	if (isNotNuloOuUndefined(colunasOcultas) && colunasOcultas.length > 0) {
		tabelaParaExportar = getTabelaComColunasOcultadas(tabela, colunasOcultas);
	} else {
		tabelaParaExportar = { ...tabela } as PublicTableComponent;
	}
	const nomeArquivo = tituloTabela;
	const headers = getXlsxHeaders(tabelaParaExportar.columns, tituloMunicipio);

	const sheetColumns = getSheetColumns(tabela!, headers, configsColunas);

	const dataExcel = {
		...getXLSXDefinitions(),
		sheet: [
			{
				...getXLSXSheetDefinitions(colunasAEsquerda),
				styleCellCondition: getStyleCellConditionXLSX(
					headers,
					tabelaParaExportar,
					configsColunas,
					colunasAEsquerda,
					linhaEhDestacada,
					colunasDestacadas
				),
				convertStringToNumber: true,
				headers: headers,
				data: getDadosXLSX(
					tabelaParaExportar,
					periodoAgrupamento,
					tituloMunicipio,
					configsColunas
				),
				columns: sheetColumns,
				title: {
					...getXLSXSheetTitleDefinitions(),
					text: nomeArquivo,
				},
			},
		],
		fileName: nomeArquivo,
	};

	ExcelTable.generateExcel(dataExcel);
}

export async function exportarMultiplasTabelasEmUnicoXLSX(
	dtoXLSX: DTOExportarMultiplasTabelasXLSX
) {
	const {
		periodoAgrupamento,
		tituloTabela,
		colunasAEsquerda,
		tabelas,
		tituloMunicipio,
		colunaIdentificadora,
		indiceColunaIdentificadora,
		configsColunas = [],
	} = dtoXLSX;
	if (!tabelas) {
		return;
	}
	const tabelasCopias = [
		...tabelas.map(t => {
			const colunas = [...t.columns];
			colunas.splice(indiceColunaIdentificadora, 0, colunaIdentificadora);
			return {
				...t,
				columns: colunas,
			};
		}),
	];
	const nomeArquivo = tituloTabela;
	const headers = getXlsxHeaders(
		[...tabelasCopias[0].columns],
		tituloMunicipio
	);
	const sheetColumns = getSheetColumns(tabelas[0], headers, configsColunas);

	const dataExcel = {
		...getXLSXDefinitions(),
		sheet: [
			{
				...getXLSXSheetDefinitions(colunasAEsquerda),
				styleCellCondition: getStyleCellConditionXLSX(
					headers,
					tabelas[0],
					configsColunas,
					colunasAEsquerda
				),
				convertStringToNumber: true,
				headers: headers,
				data: tabelasCopias
					.map(tabela => {
						return getDadosXLSX(
							tabela,
							periodoAgrupamento,
							tituloMunicipio,
							configsColunas
						);
					})
					.flat(),
				columns: sheetColumns,
				title: {
					...getXLSXSheetTitleDefinitions(),
					text: nomeArquivo,
				},
			},
		],
		fileName: nomeArquivo,
	};

	ExcelTable.generateExcel(dataExcel);
}

function temDadosParaTabela(resultados: any, tabela: any): boolean {
	return !!resultados && !!tabela;
}

function adicionarTitulo(
	documentDefinition: any,
	tituloTabela: string,
	align = 'center'
): void {
	documentDefinition.content.push({
		text: tituloTabela,
		fontSize: 12,
		alignment: align,
		margin: [0, 10],
	});
}

function adicionarImagem(
	imagem: ImagemPDF,
	documentDefinition: TDocumentDefinitions
) {
	(documentDefinition.content as Content[]).push({
		image: imagem.url,
		width: imagem?.largura,
		alignment: imagem?.alinhamento,
		marginBottom: imagem?.margemInferior,
		marginTop: imagem.margemSuperior,
	});
}

function adicionarTabelaAoDocumento(
	tabela: any,
	documentDefinition: any,
	tableData: (string | TableCell)[][]
): void {
	tabela?.columns.forEach((column: any) => {
		column.width = '*';
	});

	if (tabela && tabela?.columns.length > 9) {
		documentDefinition.pageSize = 'A3';
	}

	if (tabela && tabela?.columns.length > 20) {
		documentDefinition.pageSize = 'A2';
	}

	const larguraDinamica = `${(100 / tabela?.columns.length).toFixed(2)}%`;

	for (let i = 0; i < tableData.length; i++) {
		for (let j = 0; j < tableData[i].length; j++) {
			if (tableData[0][j] === 'Município/Posto') {
				tableData[i][j] = { text: tableData[i][j], alignment: 'left' };
			}
		}
	}

	documentDefinition.content.push({
		fontSize: tabela?.columns.length < 14 ? '10' : '9',
		table: {
			headerRows: 1,
			widths: Array(tableData[0].length).fill(larguraDinamica),
			body: tableData,
		},
		layout: {
			fillColor: (rowIndex: number, node: any, columnIndex: number) => {
				return rowIndex === 0 ? '#DCDCDC' : null;
			},
		},
	});
}

function adicionarMensagemSemDados(documentDefinition: any): void {
	documentDefinition.content.push({
		text: 'Nenhum dado encontrado na tabela',
		alignment: 'center',
		fontSize: 10,
		margin: [0, 10],
	});
}

function gerarDocumentoPDF(
	documentDefinition: any,
	nomeArquivo?: string
): void {
	const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
	if (nomeArquivo)
		setTimeout(() => {
			pdfDocGenerator.download(nomeArquivo);
		}, 100);
	pdfDocGenerator.open();
}

function prepararDadosParaTabela({
	tabela,
	periodoAgrupamento,
	isPdf,
	colunasAEsquerda,
	colunasOcultas,
	configsColunas,
}: DTOPrepararDadosParaATabela): string[][] {
	colunasOcultas = colunasOcultas ?? [];
	const titulosColunas =
		tabela?.columns
			.map(col => col.title as string)
			.filter((_, indice) => !colunasOcultas.includes(indice)) ?? [];
	const dados = [
		isPdf
			? titulosColunas.map((column, index) =>
					alinharDado(column, isPdf, 'center')
			  )
			: titulosColunas,
	];

	const tabelaTratada = { ...tabela } as PublicTableComponent;

	tabela?.data.forEach((linha: any) => {
		const dadosLinha = getDadosDaLinha({
			linha,
			titulosColunas,
			tabela: tabelaTratada,
			periodoAgrupamento,
			isPdf,
			colunasAEsquerda,
			configsColunas,
		});
		dados.push(dadosLinha);
	});

	return dados as string[][];
}

function getDadosDaLinha(dtoLinha: DTOGetDadosLinha): string[] {
	const {
		colunasAEsquerda,
		isPdf,
		periodoAgrupamento,
		linha,
		tabela,
		titulosColunas,
		configsColunas,
	} = dtoLinha;
	return titulosColunas.map((tituloColuna: string, index) => {
		let configColuna: ConfigColuna | null = null;
		if (isNotNuloOuUndefined(configsColunas) && configsColunas.length > 0) {
			configColuna = configsColunas.find(e => e.coluna === index) ?? null;
		}
		switch (tituloColuna) {
			case 'Município/Posto':
				return linha.municipioPosto ?? linha.nome;

			case 'Mês':
				if (isPdf) {
					return alinharDado(linha.mes, isPdf);
				}
				return linha.mes;

			case 'Mês/Ano':
				return formatarDataMesAno(linha, isPdf);

			case 'Data':
				if (linha.diaReferencia) {
					return alinharDado(linha.diaReferencia, isPdf);
				}
				if (linha.ano) {
					return formatarDataMesAno(linha, isPdf);
				}
				return getDadoCelula({
					linha,
					tituloColuna,
					tabela,
					periodoAgrupamento,
					isPdf,
					colunasAEsquerda: colunasAEsquerda!,
					configColuna: configColuna ?? undefined,
				});

			case 'Data/Hora ':
				return format(new Date(linha.dataColeta), 'dd/MM/yyyy HH:mm');

			default:
				return getDadoCelula({
					linha,
					tituloColuna,
					tabela,
					periodoAgrupamento,
					isPdf,
					colunasAEsquerda: colunasAEsquerda!,
					configColuna: configColuna ?? undefined,
				});
		}
	});
}

function getDadoCelula(dtoCelula: DTOGetDadoCelula): string {
	const { tabela, colunasAEsquerda, isPdf, linha, tituloColuna, configColuna } =
		dtoCelula;
	const coluna = tabela.columns.find((col: any) => col.title === tituloColuna);
	const index = tabela.columns.findIndex(
		(col: any) => col.title === tituloColuna
	);
	const render: (valor: string) => string = tabela.columns[index].render;

	let valor: string;

	const possuiAninhamentoDeObjeto =
		typeof coluna.data &&
		typeof coluna.data === 'string' &&
		coluna.data.includes('.');
	if (possuiAninhamentoDeObjeto) {
		const arrSeparadoPorPonto = coluna.data.split('.');
		let aux = '';
		for (let i = 0; i < arrSeparadoPorPonto.length; i++) {
			if (i > 0) aux += '.';
			aux += `${arrSeparadoPorPonto[i]}`;
		}
		valor = `${eval('linha.' + aux)}`;
	} else if (configColuna && configColuna.usarRender && coluna.render) {
		valor = coluna.render(linha[coluna.data]);
	} else {
		valor = linha[coluna.data];
	}

	const precisaAlinhar = coluna && coluna.data && isPdf;

	if (precisaAlinhar) {
		return (
			alinharDado(
				valor,
				isPdf,
				colunasAEsquerda.includes(index) ? 'left' : 'center'
			) ?? '-'
		);
	}
	return valor;
}

function formatarDataMesAno(row: any, isPdf: boolean): any {
	if (row['Mês/Ano']) {
		return isPdf ? alinharDado(row['Mês/Ano'], isPdf) : row['Mês/Ano'];
	}

	if (row.mes != null && row.mes !== '-') {
		const mesFormatado = String(row.mes).padStart(2, '0');
		return alinharDado(`${mesFormatado}/${row.ano}`, isPdf);
	}

	if (row.ano != null && isPdf) {
		return alinharDado(row.ano, isPdf);
	} else if (row.ano != null) {
		return row.ano;
	}

	return '-';
}

function formatarData(value: any, periodoAgrupamento: string): string | number {
	if (!value) return '-';
	const dataFormatada =
		periodoAgrupamento.toLocaleLowerCase() === 'anual' ||
		periodoAgrupamento.toLocaleLowerCase() === 'anual_parcial'
			? Number(value)
			: periodoAgrupamento.toLocaleLowerCase() === 'mensal'
			? DateTimeUtils.formatarData(String(value).substring(0, 7), 'MM/yyyy')
			: DateTimeUtils.formatarData(
					String(value).substring(0, 10),
					'dd/MM/yyyy'
			  );

	return dataFormatada;
}

function getXlsxHeaders(
	colunas: ADTColumns[],
	tituloMunicipio?: string
): Array<{ label: string; text: string; size: number }> {
	const headers: Array<{ label: string; text: string; size: number }> = [];

	if (tituloMunicipio) {
		headers.push({
			label: 'municipioPosto',
			text: 'Município/Posto',
			size: 24,
		});
	}

	colunas.forEach(coluna => {
		if (typeof coluna.data === 'string' && coluna.data.includes('.')) {
			const [atr1] = coluna.data.split('.');
			headers.push({
				label: atr1,
				text: `${coluna.title}`,
				size: 24,
			});
		} else {
			headers.push({
				label: `${isNotNuloOuUndefined(coluna.data) ? coluna.data : 'ano'}`,
				text: `${coluna.title}`,
				size: 24,
			});
		}
	});

	return headers;
}

/**
 * O mr-excel entrega ao styleCellCondition a linha já convertida para a
 * planilha, que contém apenas as colunas exportadas. A linha de origem é
 * anexada nesta chave para que as regras de destaque possam consultar campos
 * que não viraram coluna. Só as chaves dos headers são escritas no arquivo.
 */
const CHAVE_LINHA_ORIGINAL = '__linhaOriginal';

function getStyleCellConditionXLSX(
	headers: Array<{ label: string; text: string; size: number }>,
	tabela: PublicTableComponent,
	configsColunas: ConfigColuna[],
	colunasAEsquerda: number[] = [],
	linhaEhDestacada?: (linha: Record<string, any>, coluna: string) => boolean,
	colunasDestacadas?: string[]
) {
	const formatosPorColuna = headers.map(header =>
		getFormatoColunaXLSX(tabela, header.label, configsColunas)
	);
	const labelsPorColuna = headers.map(header => header.label);

	return (data: any, object: any, rowIndex: number, colIndex: number) => {
		if (data?.label) {
			return 'headerStyleCenter';
		}

		const colunaEhDestacavel =
			!colunasDestacadas ||
			colunasDestacadas.includes(labelsPorColuna[colIndex]);
		const linhaOriginal = object?.[CHAVE_LINHA_ORIGINAL] ?? object;
		const destacar =
			!!linhaEhDestacada?.(linhaOriginal, labelsPorColuna[colIndex]) &&
			colunaEhDestacavel;
		const isColunaAEsquerda = colunasAEsquerda.includes(colIndex);
		const estiloFormatado = getStyleIdByFormat(
			formatosPorColuna[colIndex],
			isColunaAEsquerda,
			destacar
		);

		if (estiloFormatado) {
			return estiloFormatado;
		}

		const estiloBase = isColunaAEsquerda ? 'rowStyle' : 'rowStyleCenter';
		return destacar ? `${estiloBase}NaoOficial` : estiloBase;
	};
}

function getDadosXLSX(
	tabela: any,
	periodoAgrupamento?: string,
	tituloMunicipio?: string,
	configsColunas: ConfigColunaXLSX[] = []
) {
	const tableData = tabela.data.map((dado: any) => {
		const novoObjeto: any = {};

		if (!configsColunas.some(c => isNotNuloOuUndefined(c.casasDecimais))) {
			for (const [key, value] of Object.entries(dado)) {
				if (isNuloOuUndefined(value)) {
					dado[key] = '-';
				}
			}
		}

		tabela.columns.forEach((coluna: any, index: number) => {
			const atributo: any = coluna.data;
			const configColuna = configsColunas.find(
				e => e.coluna === atributo || e.coluna === index
			);
			const deveUsarRender = configColuna?.usarRender && coluna.render;

			// TODO: refatorar violações do S.R.P https://medium.com/@tbaragao/solid-s-r-p-single-responsibility-principle-2760ff4a7edc
			// TODO: implementar inversão de controle, quem deve determinar como o dado é renderizado deve ser quem chama esse método genérico
			// https://medium.com/@gabrielfernandeslemos/invers%C3%A3o-de-controle-ioc-uma-abordagem-descomplicada-d01ddba4e3a4
			switch (deveUsarRender ? undefined : coluna.title) {
				case 'Mês':
					novoObjeto[atributo] = dado['mes'];
					break;
				case 'Mês/Ano':
					novoObjeto[atributo] = formatarDataMesAno(dado, false);
					break;
				case 'Data/Hora ':
					novoObjeto[atributo] = format(
						new Date(dado['dataColeta']),
						'dd/MM/yyyy HH:mm'
					);
					break;
				case 'Data':
					if (dado['ano']) {
						novoObjeto[atributo] = formatarDataMesAno(dado, false);
					} else if (
						dado['periodo'] ||
						dado['dataColeta'] ||
						dado['diaReferencia']
					) {
						const valorData =
							dado['diaReferencia'] ||
							dado['periodo'] ||
							dado['dataColeta'] ||
							dado['diaReferencia'];
						novoObjeto[atributo] = NumberUtils.ehConversivelEmNumber(valorData)
							? Number(valorData)
							: valorData;
					} else {
						novoObjeto[atributo] = formatarData(
							dado[atributo],
							periodoAgrupamento!
						);
					}
					break;
				default:
					const possuiAninhamentoDeObjeto =
						typeof atributo === 'string' && atributo.includes('.');
					if (possuiAninhamentoDeObjeto) {
						const arrSeparadoPorPonto = atributo.split('.');
						const atr1 = arrSeparadoPorPonto[0];
						let aux = '';
						for (let i = 0; i < arrSeparadoPorPonto.length; i++) {
							if (i > 0) aux += '.';
							aux += `${arrSeparadoPorPonto[i]}`;
						}
						novoObjeto[atr1] = `${eval('dado.' + aux)}`;
					} else if (dado.hasOwnProperty(atributo)) {
						if (configColuna) {
							if (isNotNuloOuUndefined(configColuna.casasDecimais)) {
								if (dado[atributo] === '-') {
									novoObjeto[atributo] = '-';
								} else {
									const valorBase = configColuna.usarRender
										? coluna.render(dado[atributo])
										: dado[atributo];
									const valorNumerico =
										typeof valorBase === 'string'
											? converterValorRenderizadoParaNumero(valorBase)
											: valorBase;
									novoObjeto[atributo] = isNuloOuUndefined(valorBase)
										? '-'
										: typeof valorNumerico === 'number'
										? Number(
												NumberUtils.getIgnorandoCasasDecimais(
													valorNumerico,
													configColuna.casasDecimais
												)
										  )
										: NumberUtils.ehConversivelEmNumber(String(valorBase))
										? Number(
												NumberUtils.getIgnorandoCasasDecimais(
													valorBase,
													configColuna.casasDecimais
												)
										  )
										: valorBase;
								}
							} else {
								novoObjeto[atributo] = deveUsarRender
									? coluna.render(dado[atributo])
									: dado[atributo];
							}
						} else {
							const result = NumberUtils.ehConversivelEmNumber(dado[atributo]);
							novoObjeto[atributo] = result
								? Number(dado[atributo])
								: dado[atributo];
						}
					} else {
						novoObjeto[atributo] = '-';
					}
			}
		});

		if (tituloMunicipio) {
			novoObjeto['municipioPosto'] = tituloMunicipio;
		}

		if (
			isNotNuloOuUndefined(dado.oficial) &&
			isNuloOuUndefined(novoObjeto.oficial)
		) {
			novoObjeto.oficial = dado.oficial;
		}

		novoObjeto[CHAVE_LINHA_ORIGINAL] = dado;

		return novoObjeto;
	});

	return tableData;
}

function converterValorRenderizadoParaNumero(valor: string) {
	if (isNuloOuUndefined(valor) || valor === '-') {
		return '-';
	}

	const valorNormalizado = String(valor).trim();
	const valorSemEspacos = valorNormalizado.replace(/\s/g, '');
	const valorSemSeparadorMilhar = valorSemEspacos.replace(
		/\.(?=\d{3}(?:\D|$))/g,
		''
	);
	const valorComPontoDecimal = valorSemSeparadorMilhar.replace(',', '.');

	return NumberUtils.ehConversivelEmNumber(valorComPontoDecimal)
		? Number(valorComPontoDecimal)
		: valor;
}
